import React, {MutableRefObject, useEffect} from 'react';
import {NodeModel} from '@minoru/react-dnd-treeview';

import {Button, ButtonToolbar, Checkbox, Icon, Menu, MenuDivider, MenuItem, Text, toaster, Modal, ListItem, Row, Col} from '@mc/ui-v4';

import classNames from 'classnames';
import {actionApiCall, rffFormSubmit} from '@mc/react-app-v4';
import {useNastaveni} from '@mc/react-app-base';
import {restData} from './TopicTreeMenu';
import {mcDelete, mcNew, mcFile, mcMoveDown, mcMoveUp} from '@mc/react-ui-icons';
import {useQueryClient} from 'react-query';
import {
    CatalogItemDetailVO,
    EnumIconType,
    GmlExportApi,
    JsonImportApi,
    JsonImportCheckVO,
    JsonImportProgressVO,
    JsonImportResultVO,
    LayerGeometryType,
    LayerSettingsApi,
    ShapefileMetadataVO,
    ShpImportApi,
    TopicTreeNodeVO,
} from '../../api';
import {CACHE_KEY_LAYER_SETTING, CACHE_KEY_LAYER_TOPIC} from '../../queryKeys';
import {UserLayerModal} from '../modals/ConfigurationModal/UserLayerModal';
import {NewEditKategorie} from '../modals/ConfigurationModal/NewEditKategorie';
import {CACHE_KEY_LAYERS_CONFIG} from '../../hooks/useLayersConfig';
import {CACHE_KEY_TOPICS_MAP} from '../../hooks/useTopicsMap';
import {CACHE_KEY_CATALOG_LAYERS} from '../../hooks/useCatalogLayers';
import {LayerId, MapEditorActions} from '@mc/react-mapio';
import {UploadShpModal} from '../modals/LayerImportModal/UploadShpModal';
import {SelectColumnsModal} from '../modals/LayerImportModal/SelectColumnsModal';
import {ImportFromLayerSchemaModal} from '../modals/LayerImportModal/ImportFromLayerSchemaModal';
import {JSON_IMPORT_TITLE, UploadJsonModal} from '../modals/JsonImportModal/UploadJsonModal';
import {ConfirmJsonImportModal} from '../modals/JsonImportModal/ConfirmJsonImportModal';
import {JsonImportProgressModal} from '../modals/JsonImportModal/JsonImportProgressModal';
import {jsonImportProgressForLayerKey} from '../modals/JsonImportModal/JsonImportLayerProgress';
import {truncate} from '../../helpers/DataMutators';
import {CACHE_KEY_MAP_SETTINGS, MapViewState} from '../../helpers/MapSettingsUtils';

type Props = {
    node: NodeModel<restData>;
    depth: number;
    isOpen: boolean;
    onToggle: () => void;
    handleSelect?: (node: number) => void;
    handleCheck?: (node: NodeModel<restData>) => void;
    isChecked?: boolean;
    indeterminate?: boolean;
    isSelected?: boolean;
    topicId: string;
    setTopicOnDelete: () => void;
    moveItem?: (data: NodeModel<restData>, index: number) => void;
    treeData: NodeModel<restData>[];
    mapRef: MutableRefObject<MapEditorActions>;
    isUserVlastnik: boolean;
    onLayerDelete: (layerId: LayerId) => void;
    /** MCW-29581 – příznak organizace; předává se shora, ať se dotaz nedrží v každém uzlu stromu. */
    atlasPristupnosti: boolean;
};

/**
 * Sestaví hlášku o výsledku importu/aktualizace včetně shrnutí načtených a chybějících obrázků.
 * U chybějících obrázků doplní nápovědu, že je lze doplnit do lokální složky a načtení zopakovat.
 */
const buildJsonResultMessage = (base: string, result?: JsonImportResultVO): string => {
    let message = base;
    if (result?.imagesLoaded) {
        message += ` Načteno obrázků: ${result.imagesLoaded}.`;
    }
    if (result?.imagesMissing) {
        message += ` Chybí obrázků: ${result.imagesMissing} – doplňte je do složky data/work/import/<id objektu> a spusťte aktualizaci znovu.`;
    }
    return message;
};

export const CustomNode: React.FC<Props> = props => {
    const {/*droppable,*/ data} = props.node;
    const {mapRef, moveItem, treeData, onToggle, isUserVlastnik, depth, atlasPristupnosti} = props;
    const queryClient = useQueryClient();
    const [savedMapViewState, setSavedMapViewState] = useNastaveni<MapViewState>(CACHE_KEY_MAP_SETTINGS, {});
    const sameParentItems = treeData.filter(item => item.parent === props.node.parent);
    const currentIndex = sameParentItems.findIndex(item => item.id === props.node.id);

    const handleDeleteVrstvu = async () => {
        await actionApiCall(async () => {
            await LayerSettingsApi.deleteCatalogItem(data.catalogItemId);
            await LayerSettingsApi.deleteLayer(data.layerId);
            await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
            await queryClient.invalidateQueries([CACHE_KEY_LAYERS_CONFIG]);
            await queryClient.invalidateQueries([CACHE_KEY_CATALOG_LAYERS, props.topicId]);
            toaster.success('Vrstva byla úspěšně smazána');
            props.onLayerDelete(data.layerId);
        });
    };
    const handleDeleteTopic = async () => {
        await actionApiCall(async () => {
            props.setTopicOnDelete();
            await LayerSettingsApi.deleteTopic(props.topicId);
            await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
            await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
            await queryClient.invalidateQueries([CACHE_KEY_CATALOG_LAYERS, props.topicId]);
            toaster.success('Topic byl úspěšně smazán');
        });
    };

    useEffect(() => {
        if (!props.isOpen) {
            onToggle();
        }
    }, []);

    const handleSelectNodeModelAfterCreate = async (catalogItemId: number): Promise<void> => {
        let layerTopicData: TopicTreeNodeVO[] = await queryClient.getQueryData([CACHE_KEY_LAYER_TOPIC, props.topicId]);
        if (!layerTopicData) {
            layerTopicData = await queryClient.fetchQuery([CACHE_KEY_LAYER_TOPIC, props.topicId]);
        }

        const layerTopic: TopicTreeNodeVO = layerTopicData.find((item: TopicTreeNodeVO): boolean => item.catalogItemId === catalogItemId);
        if (layerTopic) {
            props.handleSelect(catalogItemId);
        }
    };

    const handleCreateLayer = (parentId: number) => {
        console.log('parent', parentId);
        Modal.show(({onClose}) => (
            <UserLayerModal
                onClose={onClose}
                initialValues={{typGeometrie: LayerGeometryType.POINT, barva: 'FF9E2B', ikona: {typMarker: EnumIconType.ICON}}}
                isEdit={false}
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        const layerId = (
                            await LayerSettingsApi.createLayerWithCatalogItem(
                                props.topicId,
                                parentId.toString() === props.topicId ? undefined : +parentId,
                                data,
                            )
                        ).data;

                        const currentTopicState = savedMapViewState?.[props.topicId] ?? {};
                        const currentActiveLayers = currentTopicState.activeLayers ?? [];
                        const nextActiveLayers = currentActiveLayers.includes(layerId) ? currentActiveLayers : [...currentActiveLayers, layerId];
                        setSavedMapViewState({
                            ...savedMapViewState,
                            [props.topicId]: {
                                ...currentTopicState,
                                activeLayers: nextActiveLayers,
                            },
                        });

                        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
                        await queryClient.invalidateQueries([CACHE_KEY_CATALOG_LAYERS, props.topicId]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYERS_CONFIG]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_SETTING, layerId]);
                        // updateTopic přenačte config + katalog v klientu; setLayers pak aktivuje novou vrstvu
                        // Zprávy jdou do fronty a zpracovávají se sekvenčně – setLayers se provede po updateTopic
                        mapRef.current?.updateTopic(props.topicId);
                        mapRef.current?.setLayers(nextActiveLayers);
                        await handleSelectNodeModelAfterCreate(layerId);
                        onClose();
                    });
                }}
            />
        ));
    };

    const handleDeleteCatalogItem = async (catalogItemId: number) => {
        actionApiCall(async () => {
            await LayerSettingsApi.deleteCatalogItem(catalogItemId);
            await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
        });
    };

    const handleImportLayers = () => {
        Modal.show(({onClose}) => (
            <UploadShpModal
                onClose={onClose}
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        onClose();
                        handleSelectColumns(data);
                    });
                }}
            />
        ));
    };

    const handleImportLayerFromSchema = () => {
        Modal.show(({onClose}) => (
            <ImportFromLayerSchemaModal
                onClose={onClose}
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        console.log('data', data);
                        console.log('props', props);
                        await GmlExportApi.createVrstvaFromXsd({
                            topicId: props.topicId,
                            xsdPath: data.schema.id,
                            nazev: data.schema.name,
                            tridaObjektu: data.schema.tridaObjektu,
                        });
                        onClose();
                        toaster.success('Import byl úspěšně dokončen');
                        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
                        await queryClient.invalidateQueries([CACHE_KEY_CATALOG_LAYERS, props.topicId]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYERS_CONFIG]);
                    });
                }}
            />
        ));
    };

    const handleSelectColumns = (data: ShapefileMetadataVO) => {
        Modal.show(({onClose}) => (
            <SelectColumnsModal
                onClose={onClose}
                initialValues={data}
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        data.topicId = props.topicId;
                        data.catalogItemId = props.node?.data?.catalogItemId;
                        await ShpImportApi.importLayer(data);
                        onClose();
                        toaster.success('Import byl úspěšně dokončen');
                        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
                        await queryClient.invalidateQueries([CACHE_KEY_CATALOG_LAYERS, props.topicId]);
                        await queryClient.invalidateQueries([CACHE_KEY_LAYERS_CONFIG]);
                    });
                }}
            />
        ));
    };

    const invalidateTopicQueries = async () => {
        await queryClient.invalidateQueries([CACHE_KEY_TOPICS_MAP]);
        await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
        await queryClient.invalidateQueries([CACHE_KEY_CATALOG_LAYERS, props.topicId]);
        await queryClient.invalidateQueries([CACHE_KEY_LAYERS_CONFIG]);
    };

    const showJsonImportProgress = (title: string, successMessage: string, initialProgress: JsonImportProgressVO) => {
        Modal.show(({onClose}) => (
            <JsonImportProgressModal
                title={title}
                initialProgress={initialProgress}
                onClose={onClose}
                onFinished={async (result?: JsonImportResultVO) => {
                    toaster.success(buildJsonResultMessage(successMessage, result));
                    await invalidateTopicQueries();
                }}
                onFailed={(message?: string) => {
                    toaster.error(message || 'Import se nezdařil');
                }}
                onLayerAppear={(layerId: number) => {
                    void invalidateTopicQueries();
                    void queryClient.invalidateQueries(jsonImportProgressForLayerKey(layerId));
                }}
            />
        ));
    };

    /**
     * Import dat z JSON POV. Bez ID vrstvy se import provede do nově založené vrstvy (akce nad tématem),
     * s ID vrstvy do vrstvy existující (akce nad vrstvou).
     */
    const handleImportJsonData = (layerId?: number) => {
        Modal.show(({onClose}) => (
            <UploadJsonModal
                layerId={layerId}
                onClose={onClose}
                onSubmit={data => {
                    return rffFormSubmit(async () => {
                        onClose();
                        handleConfirmJsonImport(data);
                    });
                }}
            />
        ));
    };

    const handleConfirmJsonImport = (data: JsonImportCheckVO) => {
        Modal.show(({onClose}) => (
            <ConfirmJsonImportModal
                data={data}
                onClose={onClose}
                onConfirm={async (importNew: boolean, updateExisting: boolean) => {
                    await actionApiCall(async () => {
                        const progress = (
                            await JsonImportApi.runJsonImport({
                                layerId: data.layerId,
                                topicId: props.topicId,
                                catalogItemId: props.node?.data?.catalogItemId,
                                layerName: data.layerName,
                                filePath: data.filePath,
                                recordCount: data.recordCount,
                                importNew,
                                updateExisting,
                            })
                        ).data;
                        onClose();
                        showJsonImportProgress(JSON_IMPORT_TITLE, 'Import byl úspěšně dokončen', progress);
                    });
                }}
            />
        ));
    };

    return (
        <ListItem
            className={classNames('custom-node ', {
                skupina: props.node.droppable,
                'dragable-item': !props.node.droppable,
                'selected-node': props.isSelected,
            })}
            style={{paddingInlineStart: 7, display: 'flex', alignItems: 'center'}}
            bodyClassName="w-100"
            onClick={() => props.handleSelect(props?.node?.data?.catalogItemId ?? null)}
        >
            <Row className="d-flex align-items-center w-100 py-0" noGutters>
                <Col xs="auto pr-2">
                    {props.handleCheck && (
                        <Checkbox
                            title={'Zapnutí vrstvy po otevření tématu'}
                            checked={props.indeterminate ? undefined : props.isChecked}
                            indeterminate={props.indeterminate && !props.isChecked}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            onChange={() => props.handleCheck(props?.node)}
                        />
                    )}
                </Col>
                <Col>
                    <Text strong={props.node.droppable} title={props.node.text}>
                        {truncate(props.node.text, 33 - depth * 5)}
                    </Text>
                </Col>
                <Col className="justify-content-end">
                    {(!!props.node.data.layerId || props.node.data.topicId || !!props.node.data.catalogItemId) && (
                        <ButtonToolbar className="justify-content-end">
                            {props.node.parent !== 0 && (
                                <>
                                    <Button
                                        onClick={() => {
                                            moveItem(props.node, currentIndex - 1);
                                        }}
                                        disabled={currentIndex === 0}
                                        className={'px-0'}
                                        style={'link'}
                                        icon={<Icon icon={mcMoveUp} />}
                                    />
                                    <Button
                                        onClick={() => {
                                            moveItem(props.node, currentIndex + 1);
                                        }}
                                        disabled={currentIndex === sameParentItems.length - 1}
                                        className={'px-0'}
                                        style={'link'}
                                        icon={<Icon icon={mcMoveDown} />}
                                    />
                                </>
                            )}
                            <Button
                                inverse={props.isSelected}
                                className={'px-0'}
                                style={'link'}
                                disabled={false}
                                popupProps={{position: 'bottom right'}}
                                menu={
                                    <>
                                        {props.node?.data?.layerId ? (
                                            <>
                                                {isUserVlastnik && (
                                                    <Menu>
                                                        {/* MCW-29581 – import JSON (POV) je součástí atlasu přístupnosti */}
                                                        {atlasPristupnosti && (
                                                            <>
                                                                <MenuItem
                                                                    onClick={() => handleImportJsonData(props.node?.data?.layerId as unknown as number)}
                                                                    text={
                                                                        <Text intent={'primary'} icon={<Icon icon={mcFile} />}>
                                                                            {JSON_IMPORT_TITLE}
                                                                        </Text>
                                                                    }
                                                                />
                                                                <MenuDivider />
                                                            </>
                                                        )}
                                                        <MenuItem
                                                            onClick={handleDeleteVrstvu}
                                                            text={
                                                                <Text intent={'danger'} icon={<Icon icon={mcDelete} />}>
                                                                    Smazat vrstvu
                                                                </Text>
                                                            }
                                                        />
                                                    </Menu>
                                                )}
                                            </>
                                        ) : (
                                            <Menu>
                                                {!!props.node?.data?.catalogItemId && !props.node.data.layerId && (
                                                    <>
                                                        <div className={'p-1 px-2'}>
                                                            <Text intent={'secondary'}>Vytvořit</Text>
                                                        </div>
                                                        <MenuItem
                                                            onClick={() =>
                                                                Modal.show(({onClose}) => (
                                                                    <NewEditKategorie
                                                                        onClose={onClose}
                                                                        isEdit
                                                                        onSubmit={async data => {
                                                                            return await rffFormSubmit(async () => {
                                                                                const catalogItemDetail: CatalogItemDetailVO = {
                                                                                    catalogItemParentId: props.node?.data?.catalogItemId,
                                                                                    topicId: props.topicId,
                                                                                    label: data.nazev,
                                                                                };
                                                                                const catalogItemId = (
                                                                                    await LayerSettingsApi.createCatalogItem(catalogItemDetail)
                                                                                ).data;
                                                                                await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
                                                                                await handleSelectNodeModelAfterCreate(catalogItemId);
                                                                                onClose();
                                                                            });
                                                                        }}
                                                                    />
                                                                ))
                                                            }
                                                            text={
                                                                <Text intent={'primary'} icon={<Icon icon={mcNew} />}>
                                                                    Podkategorii
                                                                </Text>
                                                            }
                                                        />
                                                        <MenuItem
                                                            onClick={() => handleCreateLayer(props.node?.data?.catalogItemId)}
                                                            text={
                                                                <Text intent={'primary'} icon={<Icon icon={mcNew} />}>
                                                                    Vrstvu
                                                                </Text>
                                                            }
                                                        />
                                                        {isUserVlastnik && (
                                                            <>
                                                                <MenuDivider />
                                                                <MenuItem
                                                                    onClick={() => handleDeleteCatalogItem(props.node?.data?.catalogItemId)}
                                                                    text={
                                                                        <Text intent={'danger'} icon={<Icon icon={mcDelete} />}>
                                                                            Smazat kategorii
                                                                        </Text>
                                                                    }
                                                                />
                                                            </>
                                                        )}
                                                    </>
                                                )}

                                                {props.node?.data?.topicId && (
                                                    <>
                                                        <div className={'p-1 px-2'}>
                                                            <Text intent={'secondary'}>Vytvořit</Text>
                                                        </div>
                                                        <MenuItem
                                                            onClick={() =>
                                                                Modal.show(({onClose}) => (
                                                                    <NewEditKategorie
                                                                        onClose={onClose}
                                                                        onSubmit={async data => {
                                                                            return await rffFormSubmit(async () => {
                                                                                const catalogItemDetail: CatalogItemDetailVO = {
                                                                                    topicId: props.node?.data?.topicId,
                                                                                    label: data.nazev,
                                                                                };
                                                                                const catalogItemId = (
                                                                                    await LayerSettingsApi.createCatalogItem(catalogItemDetail)
                                                                                ).data;
                                                                                await queryClient.invalidateQueries([CACHE_KEY_LAYER_TOPIC, props.topicId]);
                                                                                await handleSelectNodeModelAfterCreate(catalogItemId);
                                                                                onClose();
                                                                            });
                                                                        }}
                                                                    />
                                                                ))
                                                            }
                                                            text={
                                                                <Text intent={'primary'} icon={<Icon icon={mcNew} />}>
                                                                    Kategorii
                                                                </Text>
                                                            }
                                                        />
                                                        <MenuItem
                                                            onClick={() => handleCreateLayer(props.node?.data?.topicId as unknown as number)}
                                                            text={
                                                                <Text intent={'primary'} icon={<Icon icon={mcNew} />}>
                                                                    Vrstvu
                                                                </Text>
                                                            }
                                                        />
                                                        <MenuDivider />
                                                        <MenuItem
                                                            onClick={handleImportLayers}
                                                            text={
                                                                <Text intent={'primary'} icon={<Icon icon={mcFile} />}>
                                                                    Import vrstev a číselníků
                                                                </Text>
                                                            }
                                                        />
                                                        <MenuItem
                                                            onClick={handleImportLayerFromSchema}
                                                            text={
                                                                <Text intent={'primary'} icon={<Icon icon={mcFile} />}>
                                                                    Vrstva podle schématu
                                                                </Text>
                                                            }
                                                        />
                                                        {/* MCW-29581 – import JSON (POV) je součástí atlasu přístupnosti */}
                                                        {atlasPristupnosti && (
                                                            <MenuItem
                                                                onClick={() => handleImportJsonData()}
                                                                text={
                                                                    <Text intent={'primary'} icon={<Icon icon={mcFile} />}>
                                                                        {JSON_IMPORT_TITLE}
                                                                    </Text>
                                                                }
                                                            />
                                                        )}
                                                        {isUserVlastnik && (
                                                            <>
                                                                <MenuDivider />
                                                                <MenuItem
                                                                    onClick={handleDeleteTopic}
                                                                    text={
                                                                        <Text intent={'danger'} icon={<Icon icon={mcDelete} />}>
                                                                            Smazat téma
                                                                        </Text>
                                                                    }
                                                                />
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </Menu>
                                        )}
                                    </>
                                }
                            ></Button>
                        </ButtonToolbar>
                    )}
                </Col>
            </Row>
        </ListItem>
    );
};
